import {ngAsync} from 'shared/utils/ng-@decorators';
import type {
    Auth,
    Loan,
    RawBodyMailSendRequest,
    SmsSendRequest,
    User,
    UserDetailManagerDto
} from 'typings/portaro.be.types';
import {ContactType, SenderDescriptorType} from 'typings/portaro.be.types';
import {states} from '../commons/constants/portaro.constants';
import type LoanDataService from '../commons/data-services/loan.data-service';
import type {PermissionResponse, PermissionsDataService} from '../commons/data-services/permissions.data-service';
import {PermissionsBuilder} from '../commons/data-services/permissions.builder';
import type {IPageableList, PageableListFactory} from './pageable-list.factory';
import type ReaderLoanDataService from './reader-loan.data-service';
import type UserDataService from './user.data-service';
import type { DelayedReturnPenalty } from './user.data-service';
import type UserService from './user.service';
import type {RenewableLoan} from 'typings/portaro.fe.types';
import {createRenewableLoanFromLoan} from '../loan/loan-utils';
import type ModalConversationService from '../commons/conversation/modal-conversation.service';
import type FinishedResponseInteractionService from '../commons/services/finished-response-interaction.service';
import type RemoteMvsDataService from './remote-mvs.data-service';
import type ReloaderService from '../commons/services/reloader.service';
import type LoanService from '../commons/services/loan.service';
import type WalkerService from '../commons/services/walker.service';
import type MailerService from '../commons/services/mailer.service';
import type SmsService from '../commons/services/sms.service';
import CachedPermissionsDataService from '../commons/data-services/cached-permissions.data-service';
import {transferify} from 'shared/utils/data-service-utils';
import KpDocumentMini from '../record/kp-document-mini/KpDocumentMini.svelte';
import type ModalConversationInterceptorService from '../commons/services/modal-conversation-interceptor.service';
import type {CancellationDataService} from '../commons/data-services/cancellation.data-service';
import {replaceAllById} from 'shared/utils/array-utils.fp';
import type {LocalizationService} from '../commons/providers/localization.provider';
import KpFavouritesPanel from '../commons/components/kp-favourites-panel/KpFavouritesPanel.svelte';
import {LoanInterceptorRegistrar} from '../loan/loan.interceptor-registrar';
import {UserInterceptorRegistrar} from './user.interceptor-registrar';
import type {UserPreferencesService} from './user-preferences/user-preferences.service';

export default class AccountController {
    public static readonly controllerName = 'AccountCtrl';

    public kpDocumentMiniComponent = KpDocumentMini;
    public kpFavouritesPanelComponent = KpFavouritesPanel;

    public static getUserPermissions(cachedPermissionsDataService: PermissionsDataService, userId: number): Promise<UserAccountPermissions> {
        const userSubject = {id: userId};

        const permissions = new PermissionsBuilder()
            .addPermission('LoanReturnAny')
            .addPermission('UsersShow')
            .addPermission('VisitedDocumentsUse')
            .addPermission('AuthSwitchActiveUser', userSubject)
            .addPermission('DebtDelete', userSubject)
            .addPermission('RecordCollectionsShowUsersCollections', userSubject)
            .addPermission('LoanLend', userSubject)
            .addPermission('LoanRenew', userSubject)
            .addPermission('LoanRequestMvsRemote', userSubject)
            .addPermission('PaymentDelete', userSubject)
            .addPermission('PaymentsReceiptPrint', userSubject)
            .addPermission('UserSdiRequestsShow', userSubject)
            .addPermission('UserCredentialsDelete', userSubject)
            .addPermission('UserEdit', userSubject)
            .addPermission('UserPasswordChange', userSubject)
            .addPermission('UserPreferencesEdit', userSubject)
            .addPermission('UserRegistrationAgreementPrint', userSubject)
            .addPermission('UserRegistrationPeriodExtend', userSubject)
            .addPermission('SendEmailToUser')
            .addPermission('SendSmsToUser')
            .addPermission('ShowLibrarianMessage');

        return cachedPermissionsDataService.canDoActions(permissions.build());
    }

    public hasSmsNumber: boolean;

    private readonly user: User;
    private detailManager: UserDetailManagerDto;
    public permissions: UserAccountPermissions;
    public activeLoanTypeTab: ActiveLoanTypeTab = 'loans';
    private loanList: IPageableList<Loan>;
    private waitingLoanList: IPageableList<Loan>;
    private endedLoanList: IPageableList<Loan>;
    private mvsLoanList: IPageableList<Loan>;
    private libraryActiveMvsLoanList: IPageableList<Loan>;
    private libraryPassiveMvsLoanList: IPageableList<Loan>;
    private delayedReturnPenalty: DelayedReturnPenalty;

    /*@ngInject*/
    constructor(private reloader: ReloaderService,
                private modalConversationService: ModalConversationService,
                private readerLoanDataService: ReaderLoanDataService,
                private cancellationDataService: CancellationDataService,
                private loanDataService: LoanDataService,
                private remoteMvsDataService: RemoteMvsDataService,
                private currentAuth: Auth,
                private userService: UserService,
                private walker: WalkerService,
                private pageableListFactory: PageableListFactory,
                private userDataService: UserDataService,
                private data: UserAccountControllerData,
                private finishedResponseInteractionService: FinishedResponseInteractionService,
                private loanService: LoanService,
                private mailerService: MailerService,
                private smsService: SmsService,
                private modalConversationInterceptor: ModalConversationInterceptorService,
                private localizationService: LocalizationService,
                private userPreferencesService: UserPreferencesService,
    ) {
        this.user = data.user;
        this.detailManager = data.detailManager;
        this.permissions = data.permissions;
        this.init();
    }

    public init() {
        this.loanList = this.pageableListFactory.create(
            (pageable) => this.readerLoanDataService.getAllLoans(this.user.id, pageable),
            () => this.readerLoanDataService.getAllLoansSize(this.user.id),
            this.loanDataService.getById.bind(this.loanDataService)
        );
        this.waitingLoanList = this.pageableListFactory.create(
            (pageable) => this.readerLoanDataService.getAllWaitingLoans(this.user.id, pageable),
            () => this.readerLoanDataService.getAllWaitingLoansSize(this.user.id),
            this.loanDataService.getById.bind(this.loanDataService)
        );
        this.endedLoanList = this.pageableListFactory.create(
            (pageable) => this.readerLoanDataService.getAllEndedLoans(this.user.id, pageable),
            () => this.readerLoanDataService.getAllEndedLoansSize(this.user.id),
            this.loanDataService.getById.bind(this.loanDataService)
        );
        this.mvsLoanList = this.pageableListFactory.create(
            (pageable) => this.readerLoanDataService.getAllMvsLoans(this.user.id, pageable),
            () => this.readerLoanDataService.getAllMvsLoansSize(this.user.id),
            this.loanDataService.getById.bind(this.loanDataService)
        );
        this.libraryActiveMvsLoanList = this.pageableListFactory.create(
            (pageable) => this.readerLoanDataService.getAllLibraryActiveMvsLoans(this.user.id, pageable),
            () => this.readerLoanDataService.getAllLibraryActiveMvsLoansSize(this.user.id),
            this.loanDataService.getById.bind(this.loanDataService)
        );
        this.libraryPassiveMvsLoanList = this.pageableListFactory.create(
            (pageable) => this.readerLoanDataService.getAllLibraryPassiveMvsLoans(this.user.id, pageable),
            () => this.readerLoanDataService.getAllLibraryPassiveMvsLoansSize(this.user.id),
            this.loanDataService.getById.bind(this.loanDataService)
        );

        this.loadDelayedReturnPenalty();

        if (this.detailManager.loansShowing) {
            this.initLoans();
        }

        this.hasSmsNumber = !!(this.user.contacts?.filter((contact) => contact.type.id === ContactType.SMS_PHONE)?.[0]);

        new LoanInterceptorRegistrar(this.modalConversationInterceptor, this.localizationService).register();
        new UserInterceptorRegistrar(this.modalConversationInterceptor).register();
    }

    public initLoans() {
        this.loanList.loadSize();
        this.loanList.loadItems();

        this.waitingLoanList.loadSize();
        this.endedLoanList.loadSize();

        if (this.user.role.includes('ROLE_LIBRARY')) {
            this.libraryActiveMvsLoanList.loadSize();
            this.libraryPassiveMvsLoanList.loadSize();
        } else {
            this.mvsLoanList.loadSize();
        }
    }

    public editUser() {
        return this.userService.editUserByForm(this.user);
    }

    @ngAsync()
    public async userPreferences() {
        await this.userPreferencesService.setUserPreferences(this.user);
    }

    @ngAsync()
    public async showPasswordChangeForm() {
        return this.userService.showPasswordChangeForm(this.user);
    }

    @ngAsync()
    public async deleteCredentials() {
        await this.userService.deleteCredentials(this.user);
        this.reloader.reload();
    }

    @ngAsync()
    public async extendRegistration() {
        await this.userService.extendRegistration(this.user);
        this.reloader.reload();
    }

    // eslint-disable-next-line @typescript-eslint/require-await
    @ngAsync()
    public async cancelLoan(loan) {
        let promise;
        if (loan.loanRequestId > 0) {
            promise = this.cancellationDataService.cancelLoanRequest(loan);
        } else {
            promise = this.cancellationDataService.cancelLoan(loan);
        }
        return promise
            .then((response) => this.finishedResponseInteractionService.showSuccessInToast(response))
            .then(this.waitingLoanList.reloadItems.bind(this.waitingLoanList))
            .catch((response) => this.finishedResponseInteractionService.showFailedInToast(response));

    }

    @ngAsync()
    public async renewAllLoans() {
        try {
            const result = await this.loanDataService.renewAllLoans(this.user);
            await this.finishedResponseInteractionService.showSuccessInModalWindow(result);
        } catch (result) {
            await this.finishedResponseInteractionService.showFailedInModalWindow(result);
        }
        await this.loanList.reloadItems();
    }

    @ngAsync()
    public async renewLoan(loan: Loan) {
        loan = createRenewableLoanFromLoan(loan, {
            state: states.PROCESSING,
            text: null,
            success: null
        });

        const renewalResponse = await this.loanDataService.renewLoan(loan);
        const reloadedLoan: Loan = await this.loanDataService.getById(loan.id);
        const renewedLoan: RenewableLoan = createRenewableLoanFromLoan(reloadedLoan,
            {
                state: states.NORMAL,
                text: renewalResponse.text,
                success: renewalResponse.finished
            }
        );
        replaceAllById(renewedLoan)(this.loanList.items);
    }

    @ngAsync()
    public async showLendingForm() {
        const successResponses = await this.loanService.showLendingForm(this.user);

        if (successResponses.length > 0) {
            await Promise.all([this.loanList.reloadItems(), this.waitingLoanList.reloadItems()]);
        }
    }

    @ngAsync()
    public async showReturningForm() {
        try {
            await this.loanService.showReturningForm();
        } finally {
            await Promise.all([this.loanList.reloadItems(), this.endedLoanList.reloadItems()]);
        }
    }

    @ngAsync()
    public async returnLoan(loan): Promise<void> {
        try {
            await this.loanService.returnLoanAndShowResult(loan);
        } finally {
            await Promise.all([this.loanList.reloadItems(), this.endedLoanList.reloadItems()]);
        }
    }

    @ngAsync()
    public async loadDelayedReturnPenalty() {
        this.delayedReturnPenalty = await this.userService.getDelayedReturnPenalty(this.user);
    }

    public async sendRemoteMvs() {
        try {
            const response = await this.remoteMvsDataService.send({requester: this.user});
            await this.finishedResponseInteractionService.showSuccessResponseInToastIfPossible(response);
            await this.mvsLoanList.reloadItems();
        } catch (exceptionResponse) {
            this.finishedResponseInteractionService.showFailedResponseInToast(exceptionResponse);
        }
    }

    public printReceiptOfPayment() {
        this.walker.newPageInNewWindow('/users/print-receipt-of-payment', transferify({user: this.user}))
    }


    public printRegistrationAgreement() {
        this.walker.newPageOnContext(`/users/${this.user.id}/print-registration-agreement`);
    }

    public async loadLoans() {
        await this.loanList.loadItems();
        this.activeLoanTypeTab = 'loans';
    }

    public async loadWaitingLoans() {
        await this.waitingLoanList.loadItems();
        this.activeLoanTypeTab = 'waiting-loans';
    }

    public async loadEndedLoans() {
        await this.endedLoanList.loadItems();
        this.activeLoanTypeTab = 'ended-loans';
    }

    public async loadMvsLoans() {
        await this.mvsLoanList.loadItems();
        this.activeLoanTypeTab = 'mvs-loans';
    }

    public async loadLibraryActiveMvsLoans() {
        await this.libraryActiveMvsLoanList.loadItems();
        this.activeLoanTypeTab = 'library-active-mvs-loans';
    }

    public async loadLibraryPassiveMvsLoans() {
        await this.libraryPassiveMvsLoanList.loadItems();
        this.activeLoanTypeTab = 'library-passive-mvs-loans';
    }

    public async sendEmail() {
        const request = {
            senderType: SenderDescriptorType.SYSTEM,
            recipientEmails: [this.user.email]
        } as RawBodyMailSendRequest;
        return this.mailerService.sendMail(request);
    }

    public async sendSms() {
        const request = {recipients: [this.user]} as SmsSendRequest;
        return this.smsService.sendSms(request);
    }

    public clearPermissionsCache() {
        CachedPermissionsDataService.clearCache();
    }

}

export interface UserAccountPermissions extends PermissionResponse {
    LoanReturn: boolean;
    UsersShow: boolean;
    VisitedDocumentsUse: boolean;
    AuthSwitchActiveUser: boolean;
    DebtDelete: boolean;
    RecordCollectionsShowUsersCollections: boolean;
    LoanLend: boolean;
    LoanRenew: boolean;
    LoanRequestMvsRemote: boolean;
    PaymentDelete: boolean;
    PaymentsReceiptPrint: boolean;
    UserSdiRequestsShow: boolean;
    UserCredentialsDelete: boolean;
    UserEdit: boolean;
    UserPasswordChange: boolean;
    UserPreferencesEdit: boolean;
    UserRegistrationAgreementPrint: boolean;
    UserRegistrationPeriodExtend: boolean;
    SendEmailToUser: boolean;
    SendSmsToUser: boolean;
    ShowLibrarianMessage: boolean;
}

export interface UserAccountControllerData {
    user: User;
    detailManager: UserDetailManagerDto;
    permissions: UserAccountPermissions;
}

type ActiveLoanTypeTab = 'loans' | 'waiting-loans' | 'ended-loans' | 'mvs-loans' | 'library-passive-mvs-loans' | 'library-active-mvs-loans';